import React, { useState, useEffect, useRef, useCallback } from 'react';
import Vimeo from '@u-wave/react-vimeo';

export function CourseClassroomPlayerVimeo(props) {
  const { disclaimerModal, setFinishVideoModal, unansweredQuestions, videoId, onUpdateProgress, setUpdateContentNow, selectedItem, showQuizModal, setShowQuizModal, setCurrentQuiz, onFetchLearnSystemUserItem } = props;
  const [finished, setFinished] = useState(false);
  const [currentPercent, setCurrentPercent] = useState();
  const [count, setCount] = useState(0);
  const playerRef = useRef(null);

  const parsedQuiz = selectedItem?.['quiz-items']?.map(item => {
    const time = item.duration;
    const parts = time.split(':');
    const hours = parseInt(parts[0], 10) * 3600;
    const minutes = parseInt(parts[1], 10) * 60;
    const seconds = parseInt(parts[2], 10);
    const totalSeconds = hours + minutes + seconds;
    return {
      body: item.body,
      id: item.id,
      duration: totalSeconds,
      'quiz-item-alternatives': item['quiz-item-alternatives'],
      taxonomies: item.taxonomies
    };
  });

  const openModalTimings = parsedQuiz?.map(item => item.duration);

  useEffect(() => {
    setFinished(false);
    setCount(0); // Resetar count ao mudar de vídeo
    setCurrentPercent(0); // Resetar progresso ao mudar de vídeo
    playerRef.current = null; // Resetar playerRef ao mudar de vídeo, para evitar interações com player antigo
  }, [videoId]);

  // Usar useCallback para onReady e onTimeUpdate para evitar recriação desnecessária
  // e garantir que eles sempre usem a ref mais atualizada.
  const onReady = useCallback(
    player => {
      playerRef.current = player; // Salva a instância do player na ref

      // Tentar dar play assim que o player estiver pronto, se as condições permitirem
      // Removido o catch aqui para a mensagem de erro ser mais clara,
      // pois o autoplay já é mais controlado.
      if (!showQuizModal && !disclaimerModal) {
        playerRef.current.play().catch(e => console.warn('Erro ao tentar play no onReady (pode ser bloqueio do navegador):', e));
      }
    },
    [videoId, showQuizModal, disclaimerModal]
  );

  useEffect(() => {
    // Usar a referência atual do player para interagir
    if (playerRef.current) {
      if (!showQuizModal || !disclaimerModal) {
        playerRef.current.play().catch(e => console.warn('Erro ao tentar play após modal/disclaimer:', e));
      } else {
        playerRef.current.pause().catch(e => console.warn('Erro ao tentar pause após modal/disclaimer:', e));
      }
    }
  }, [showQuizModal, disclaimerModal]);

  const handlePlay = () => {
    setTimeout(() => setCount(0), 2000);
  };

  const onTimeUpdate = useCallback(
    async e => {
      // Só continue se houver um player válido
      if (!playerRef.current) return;

      const percent = parseInt(e.percent * 100);
      const videoCurrentTime = parseInt(e.seconds);
      const videoDuration = parseInt(e.duration);

      // Lógica para modal de vídeo finalizado com perguntas não respondidas
      if (percent && videoCurrentTime === videoDuration - 4 && unansweredQuestions.length > 0 && count === 0) {
        setFinishVideoModal(true);
        setShowQuizModal(true);
        setCurrentQuiz(unansweredQuestions[0]);
        try {
          await playerRef.current.pause(); // Pausa o player
          setCount(prevState => prevState + 1); // Atualiza o contador para evitar repetição
        } catch (err) {
          console.error('Erro ao pausar o player para o modal de finalização:', err);
        }
      }

      // Lógica para abrir quiz em momentos específicos
      if (openModalTimings?.includes(Math.floor(e.seconds)) && count === 0) {
        try {
          await playerRef.current.pause(); // Pausa o player
          setShowQuizModal(true);
          setCount(count + 1);
          setCurrentQuiz(parsedQuiz.find(item => item.duration === Math.floor(e.seconds)));

          if (document.fullscreenElement !== null) {
            document.exitFullscreen();
          }
        } catch (err) {
          console.error('Erro ao pausar o player para o quiz ou sair do fullscreen:', err);
        }
      }

      // Lógica de atualização de progresso
      if (!finished) {
        if (currentPercent !== percent && percent >= 90) {
          setFinished(true);
          setCurrentPercent(percent);
          await onUpdateProgress(percent);
          await onFetchLearnSystemUserItem();
        } else if (currentPercent !== percent && percent > 0 && percent % 5 === 0) {
          setCurrentPercent(percent);
          await onUpdateProgress(percent);
        }
      }

      // Lógica para atualizar conteúdo ao 100% (pode ser redundante com o >=90, verifique sua necessidade)
      if (percent === 100 && videoCurrentTime === videoDuration) {
        setUpdateContentNow(true);
      }
    },
    [finished, currentPercent, unansweredQuestions, count, openModalTimings, parsedQuiz, setFinishVideoModal, setShowQuizModal, setCurrentQuiz, onUpdateProgress, onFetchLearnSystemUserItem, setUpdateContentNow]
  );

  if (!videoId) {
    return <div>Carregando vídeo...</div>;
  }

  return (
    <Vimeo
      key={videoId}
      onReady={onReady}
      onPlay={handlePlay}
      video={videoId}
      autoplay={false}
      height="100%"
      width="100%"
      onTimeUpdate={onTimeUpdate}
      onError={error => console.error('Erro fatal do Vimeo Player:', error)}
    />
  );
}
